import { useEffect, useState } from 'react'
import { createModal } from '../ModalFactory'
import { AuthContext, useAuth } from 'context/AuthContext'
import { ModalWrapper } from '../ModalWrapper'
import {
    TabStrip,
    TabStripSelectEventArguments,
    TabStripTab,
} from '@progress/kendo-react-layout'
import GeneralForm from './GeneralForm'
import Orders from './Orders'
import Translation from 'components/Translation'
import Appointments from './Appointments'
import Memberships from './Memberships'
import { useRouter } from 'next/router'
import HistoryLogs from './History'
import { getApiRequest, patchApiRequest, postApiRequest, putApiRequest, useFetch } from 'utils/fetch-helper'
import { FetchMethods } from 'utils/types'
import { useTenantContext } from 'context/TenantContext'
import AddressForm from './AddressForm'
import { useAppContext } from 'context/AppContext'
import { GlobalDialogType } from '../GlobalDialog'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import DialogModal from '../Dialog'
import { getTranslation } from 'utils/language-helper'
import { ClientApi } from './types'
import ConfirmDialogModal from '../ConfirmDialog'
import { SubmitError, UserModel } from '../types'
import { toYmd } from 'utils/string-localization-helper'
import SavingStates, { SavingStatus } from 'components/Spinners/Saving'
import ViewResponse, { ViewResponseStatus } from '../IntakeForms/ViewResponse'
import { IntakeClientResponse, IntakeForm, IntakeFormDTO, IntakeResponseDTO, mapIntakeFormFromDTO, mapIntakeResponseFromDTO } from 'types/intake-forms'

const ModalProvider = createModal(
    AuthContext,
    'CreateClientModal',
    () => (
        <>
            <i className="feather feather-plus mr-4" />
            <span className="circular">Create</span>
        </>
    ),
    () => (
        <span className="inline-block w-6 h-6 text-primary text-center font-extralight">
            <i className="feather feather-chevron-left " />
        </span>
    )
)

export const CreateClientModalOpener = ModalProvider.Opener
export const CreateClientModalCloser = ModalProvider.Closer

export default function CreateClientModal() {
    const router = useRouter()
    const { setGlobalError } = useAppErrorApiContext()
    const ctx = useAuth()
    const {
        translations,
        loadComponentTranslations,
        setGlobalLoading,
    } = useAppContext()
    const { tenant } = useTenantContext()
    const { CreateClientModal } = useAuth()
    const { attributes, setAttributes } = CreateClientModal
    const [selected, setSelected] = useState<number>(0)
    const [status, setStatus] = useState<SavingStatus>(SavingStatus.NONE)
    const [view_form_index, setViewFormIndex] = useState<number>(0)
    const [client_responses, setClientResponses] = useState<IntakeClientResponse[]>([])
    const [intake_forms, setIntakeForms] = useState<IntakeForm[]>([])

    const handleSelect = (e: TabStripSelectEventArguments) => {
        setSelected(e.selected)
        if (selected != e.selected && CreateClientModal.attributes?.isDirty) {
            onSubmit()
        }
        setStatus(SavingStatus.NONE)
    }

    const email = CreateClientModal.attributes?.email as string
    const isNew = CreateClientModal.attributes?.is_new as boolean
    const isEdit = !!CreateClientModal.attributes?.id

    const [prompt_message, toggleDialog] = useState<string>('')
    const [prompt_merge_message, setPromptMergeMessage] = useState('')
    const [payload, setPayload] = useState<ClientApi>()
    const { data: client_dto } = useFetch(
        `/v1/clients/by-tenant-id-and-email?email=${encodeURIComponent(
            email as string
        )}&tenantId=${tenant?.id}`,
        FetchMethods.GET,
        !!email && !!tenant?.id && !attributes?.original_email && !isNew
    )

    const onSubmit = async () => {
        try {
            setGlobalLoading(true)

            const user: UserModel = {
                firstName: CreateClientModal.attributes?.first_name as string,
                lastName: CreateClientModal.attributes?.last_name as string,
                email: CreateClientModal.attributes?.email as string,
                phone: CreateClientModal.attributes?.phone as string,
                preferredLanguageId: CreateClientModal.attributes?.preferredLanguageId as string,
                street1: CreateClientModal.attributes?.street1 as string,
                state: CreateClientModal.attributes?.state as string,
                city: CreateClientModal.attributes?.city as string,
                zip: CreateClientModal.attributes?.zip as string,
                country: CreateClientModal.attributes?.country as string,
                timezone: CreateClientModal.attributes?.timezone as string,
                cognitoId: CreateClientModal.attributes?.cognito_id as string,
                imageUrl: CreateClientModal.attributes?.image_url as string,
            }
            if (CreateClientModal.attributes?.date_of_birth) {
                user.dateOfBirth = toYmd(new Date(CreateClientModal.attributes?.date_of_birth as Date))
            }
            user.id = CreateClientModal.attributes?.user_id as string
            const payload = {
                tenant,
                user,
                notes: CreateClientModal.attributes?.notes as string,
                cognitoId: CreateClientModal.attributes?.cognito_id as string,
            }

            setStatus(SavingStatus.SAVING)
            if (CreateClientModal.attributes?.id) {
                const putReq = await putApiRequest(
                    `/v1/clients/${CreateClientModal.attributes?.id}`,
                    payload
                )
                if (putReq && putReq?.status === 400) {
                    if (
                        putReq?.tag == 'client.error.cognito_id_or_email_already_exists' ||
                        putReq?.tag == 'client.error.email_already_exists'
                    ) {
                        setStatus(SavingStatus.NONE)
                        setPayload(payload)
                        setPromptMergeMessage(
                            getTranslation(
                                'clients.merge_client_prompt',
                                translations
                            )
                        )
                    } else {
                        throw new Error(putReq?.message)
                    }
                } else {
                    if (putReq) {
                        setStatus(SavingStatus.SAVED_SUCCESS)
                        return putReq
                    }
                }
            } else {
                const res = await postApiRequest('/v1/clients', payload)

                if (res && res.status === 400) {
                    throw new Error(res?.message)
                } else {
                    if (res) {
                        setStatus(SavingStatus.SAVED_SUCCESS)
                        return res
                    }
                }
            }

        } catch (e: unknown) {
            const { name, message } = e as SubmitError
            setStatus(SavingStatus.SAVED_FAILURE)
            setGlobalError({ message })
        } finally {
            setGlobalLoading(false)
        }
    }

    const archiveClient = async (id: string, first_name: string, last_name: string) => {
        try {
            setGlobalLoading(true)
            const success: string[] = []
            const failure: string[] = []
            const api = await putApiRequest(
                `/v1/clients/soft-delete/${id}`
            )

            if (api.ok) {
                success.push(id)
            } else {
                failure.push(id)
            }

            let title = ''
            if (success.length) {
                title += `archive_client_success`
            }
            if (failure.length) {
                title += `archive_client_fail`
            }
            ctx.GlobalDialog.setAttributes({
                title: title,
                message: success.length
                    ? `${first_name} ${last_name}`
                    : '',
                button: 'back_to_client',
                className: 'w-[28rem]',
                type: success.length
                    ? GlobalDialogType.SUCCESS
                    : GlobalDialogType.ALERT,
            })
            ctx.GlobalDialog.open()

            ctx.CreateClientModal.setAttributes({
                archive_client: true,
            })
        } catch (err) {
            setGlobalError(err)
        } finally {
            setGlobalLoading(false)
        }
    }

    const mergeClient = async () => {
        try {
            if (payload?.user?.email) {
                setGlobalLoading(true)
                let original = await getApiRequest(
                    `/v1/clients/by-tenant-id-and-email?email=${encodeURIComponent(
                        payload.user.email
                    )}&tenantId=${tenant?.id}`
                )
                if (original?.id && CreateClientModal.attributes?.id) {
                    let res = await patchApiRequest(
                        `/v1/clients/${CreateClientModal.attributes?.id}?sourceId=${original.id}`,
                        payload
                    )
                    if (res?.ok) {
                        ctx.GlobalDialog.setAttributes({
                            title: 'clients.merge_client_success',
                            button: 'back_to_client',
                            className: 'w-[28rem]',
                            type: GlobalDialogType.SUCCESS,
                            messageClassName: 'hidden',
                        })
                        ctx.GlobalDialog.open()
                        ctx.CreateClientModal.setAttributes({
                            has_updates: true,
                        })
                        ctx.CreateClientModal.close()
                    } else {
                        throw res
                    }
                }
            }
        } catch (e) {
            setGlobalError(e)
        } finally {
            setGlobalLoading(false)
        }
    }

    function closeModal(message: string) {
        ctx.CreateClientModal.setAttributes({
            has_updates: true,
        })
        ctx.CreateClientModal.close()

        ctx.GlobalDialog.setAttributes({
            title: message,
            className: 'w-1/2 overflow-hidden',
            type: GlobalDialogType.SUCCESS,
        })
        ctx.GlobalDialog.open()
    }

    useEffect(() => {
        if (!ctx.CreateClientModal.is_open) {
            setSelected(0)
            setStatus(SavingStatus.NONE)
            setPromptMergeMessage('')
            setClientResponses([])
            setIntakeForms([])
            setViewFormIndex(0)

            router.replace({
                pathname: router.pathname,
                query: {
                    ...router.query,
                    clientId: null
                },
            })
        }
    }, [ctx.CreateClientModal.is_open])

    useEffect(() => {
        if (client_dto && client_dto.user) {
            const {
                id: user_id,
                email,
                firstName: first_name,
                lastName: last_name,
                phone,
                preferredLanguageId: preferred_language_id,
                street1,
                state,
                city,
                country,
                zip,
                timezone,
                imageUrl: image_url
            } = client_dto.user
            CreateClientModal.setAttributes({
                ...CreateClientModal.attributes,
                id: client_dto.id,
                cognito_id: client_dto.cognitoId || client_dto.user.cognitoId,
                user_id,
                email,
                first_name,
                last_name,
                phone,
                date_of_birth: client_dto.user.dateOfBirth ? new Date(client_dto.user.dateOfBirth) : '',
                preferred_language_id,
                notes: client_dto.notes,
                street1,
                state,
                city,
                country,
                zip,
                timezone,
                image_url,
                original_email: email
            })

            router.replace({
                pathname: router.pathname,
                query: { clientID: client_dto.id },
            })
        }
    }, [client_dto])

    useEffect(() => {
        if (!translations?.CLIENTS_TRANSLATION_ID) {
            loadComponentTranslations('CLIENTS_TRANSLATION_ID')
        }
    }, [translations])

    useEffect(() => {
        if (attributes?.show_responses) {
            if (attributes && attributes.response_order_id as string) {
                fetchResponses(attributes.response_order_id as string)
            } else {
                setAttributes({
                    ...attributes,
                    show_responses: false
                })
            }
        }
    }, [attributes?.response_order_id, attributes?.show_responses])

    async function fetchResponses(order_id: string) {
        try {
            setGlobalLoading(true)
            let intake_forms: IntakeForm[] = []
            let client_responses: IntakeClientResponse[] = []
            let responses: IntakeResponseDTO[] = await getApiRequest(`/v1/intake-forms/responses/by-order-id?orderId=${order_id}`)
            if (responses?.length > 0) {
                for (const response of responses) {
                    let intake_form: IntakeFormDTO = await getApiRequest(`/v1/intake-forms/by-id?id=${response.intakeFormId}`)
                    if (intake_form) {
                        let mapped = mapIntakeFormFromDTO(intake_form)
                        intake_forms.push(mapped)
                    }
                    let client_response = {
                        client: { //currently don't need this but if requested just update
                            user: {
                                first_name: attributes?.first_name || "",
                                last_name: attributes?.last_name || "",
                                email: attributes?.email || "",
                                phone: attributes?.phone || ""
                            }
                        },
                        response: mapIntakeResponseFromDTO(response)
                    } as IntakeClientResponse
                    client_responses.push(client_response)
                }

            }
            setClientResponses(client_responses)
            setIntakeForms(intake_forms)
        } catch (error) {

        } finally {
            setGlobalLoading(false)
        }

    }

    return (
        <ModalProvider.Visible>
            <ModalWrapper>
                <div className="bg-white shadow-2xl sm:rounded-md relative border-2 border-gray-100 my-auto addnew-client">
                    <DialogModal
                        prompt_message={prompt_message}
                        onCloseModal={() => {
                            toggleDialog('')
                        }}
                    />
                    <div className="flex px-4 z-10 w-full pt-4 bg-opacity-30 pb-3 bg-primary-lighter">
                        <div>
                            <ul className="ml-4 text-sm flex gap-1 breadcrumbs mb-2">
                                <Translation
                                    render_as="li"
                                    content_key="dashboard"
                                />
                                <Translation
                                    render_as="li"
                                    content_key="clients"
                                />
                            </ul>
                            <span className="text-lg text-primary-dark">
                                <span
                                    onClick={() => {
                                        CreateClientModal.setAttributes({})
                                        setSelected(0)
                                        router.replace({
                                            pathname: router.pathname,
                                            query: null,
                                        })
                                    }}
                                >
                                    <CreateClientModalCloser className="self-center pt-1" />
                                </span>
                                <span className="circular ml-3">
                                    {isEdit ? (
                                        `${CreateClientModal.attributes?.first_name} ${CreateClientModal.attributes?.last_name}`
                                    ) : (
                                        <Translation content_key="new_client" />
                                    )}
                                </span>
                            </span>
                        </div>
                    </div>
                    <TabStrip
                        selected={selected}
                        onSelect={handleSelect}
                        className="z-20"
                        animation={false}
                    >
                        <TabStripTab
                            title={<Translation content_key="general" />}
                        >
                            <div className="p-5 w-full">
                                <GeneralForm
                                    archiveClient={(id: string, first_name: string, last_name: string) => {
                                        archiveClient(id, first_name, last_name)
                                    }}
                                    onFormSubmit={async () => {
                                        const bool = await onSubmit()
                                        if (bool) {
                                            const message = bool.updatedAt ? 'clients.successfully_updated' : 'clients.successfully_added'
                                            closeModal(message)
                                        }
                                    }}
                                />
                                <SavingStates
                                    status={status}
                                />
                            </div>
                        </TabStripTab>

                        {!isNew && (<TabStripTab
                            title={<Translation content_key="address" />}
                            disabled={!isEdit}
                        >
                            <div className="p-5 w-full">
                                <AddressForm
                                    archiveClient={(id: string, first_name: string, last_name: string) => {
                                        archiveClient(id, first_name, last_name)
                                    }}
                                    onFormSubmit={async () => {
                                        const bool = await onSubmit()
                                        if (bool) {
                                            const message = bool.updatedAt ? 'clients.successfully_updated' : 'clients.successfully_added'
                                            closeModal(message)
                                        }
                                    }}
                                />
                                <SavingStates
                                    status={status}
                                />
                            </div>
                        </TabStripTab>)}

                        {/* Conditionally render the "Appointments" tab based on isNew */}
                        {!isNew && (
                            <TabStripTab
                                title={<Translation content_key="appointments" />}
                                disabled={!isEdit}
                            >
                                <div className="p-5 min-h-[300px] w-full">
                                    <Appointments />
                                </div>
                            </TabStripTab>
                        )}

                        {/* Conditionally render the "Memberships" tab based on isNew */}
                        {!isNew && (
                            <TabStripTab
                                title={<Translation content_key="memberships" />}
                                disabled={!isEdit}
                            >
                                <div className="p-5 min-h-[300px] w-full">
                                    <Memberships />
                                </div>
                            </TabStripTab>
                        )}

                        {/* Conditionally render the "Orders" tab based on isNew */}
                        {!isNew && (
                            <TabStripTab
                                title={<Translation content_key="orders" />}
                                disabled={!isEdit}
                            >
                                <div className="p-5 w-full">
                                    <Orders />
                                </div>
                            </TabStripTab>
                        )}

                        {/* Conditionally render the "History" tab based on isNew */}
                        {!isNew && (
                            <TabStripTab
                                title={<Translation content_key="history" />}
                                disabled={!isEdit}
                            >
                                <div className="p-5 w-full">
                                    <HistoryLogs />
                                </div>
                            </TabStripTab>
                        )}
                    </TabStrip>
                    <ConfirmDialogModal
                        prompt_message={prompt_merge_message}
                        onConfirm={mergeClient}
                        onCancel={() => {
                            setPromptMergeMessage('')
                        }}
                    />
                    <ViewResponse
                        status={{ show: !!(attributes?.show_responses as boolean) && client_responses.length > 0 && client_responses[0].response.order_id == attributes?.response_order_id }}
                        toggleStatus={(status: ViewResponseStatus) => {
                            setAttributes({
                                ...attributes,
                                show_responses: status.show
                            })
                        }}
                        form={intake_forms[view_form_index]}
                        response={client_responses[view_form_index]}
                        view_form_index={view_form_index}
                        setViewFormIndex={setViewFormIndex}
                        total_forms_count={intake_forms.length}
                        show_client_details={true}
                    />
                </div>
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}
